import { Box, Button, FormControl, IconButton, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { apiUrl, useApiRequest } from '../store/Common';
import moment from 'moment/moment';
import { ClipLoader } from 'react-spinners';
import { Link } from 'react-router-dom';
import { Close, SearchTwoTone } from '@mui/icons-material';
import { DateRangePicker } from 'rsuite';

function RecurringInvoicePage() {

    const {postData} = useApiRequest();
    const [page, setPage] = useState(2);
    const [totalDocs, setTotalDocs] = useState(0);
    const [invoicesList, setInvoicesList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const containerRef = useRef();
    const [bodyData, setBodyData] = useState({status: '', search: '', start: 0, end: 0, dtype: "created"});

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedType, setSelectedType] = useState('created');
    const [selectedStatus, setSelectedStatus] = useState('');

    useEffect(()=>{
        const getOneData = async() =>{
            setIsLoading(true);
            setInvoicesList([]);
            const resp = await postData('invoice/listsrecurring/1', {status: '', search: '', start: 0, end: 0, dtype: "created"});
            if(resp){
                setInvoicesList(resp.data.docs);
                setPage(resp.data.nextPage);
                setTotalDocs(resp.data.totalDocs);
            }
            setIsLoading(false);
        }
        getOneData();
    },[postData]);

    const debounce = (func, delay) => {
        let timer;
        return function() {
            const context = this;
            const args = arguments;
            clearTimeout(timer);
            timer = setTimeout(() => {
                func.apply(context, args);
            }, delay);
        };
    };
    const getData = useCallback(async(page, body) =>{
        setIsLoading(true);
        const resp = await postData('invoice/listsrecurring/'+page, body);
        if(resp){
            setInvoicesList((prevArray) => [...prevArray, ...resp.data.docs]);
            setPage(resp.data.nextPage);
            setTotalDocs(resp.data.totalDocs);
        }
        setIsLoading(false);
    },[postData]);

    const handleScroll = useCallback(() => {
        const currentContainer = containerRef.current;
        if (currentContainer && invoicesList.length > 0 && !isLoading) {
            const scrollOffset = currentContainer.scrollHeight - (currentContainer.scrollTop + currentContainer.clientHeight);
            if(currentContainer.scrollTop > 0 && scrollOffset >= 0 && scrollOffset < 10 && page){
                getData(page, bodyData);
            }
        }
    },[getData, isLoading, invoicesList.length, page, bodyData])
    
    //on scroll trigger
    useEffect(() => {
        const currentContainer = containerRef.current;
        const debouncedScrollHandler = debounce(handleScroll, 500);
        currentContainer.addEventListener('scroll', debouncedScrollHandler);
        return () => {
            currentContainer.removeEventListener('scroll', debouncedScrollHandler);
        };
    }, [handleScroll]);

    const resetList = () =>{
        if(isLoading){
            return;
        }
        setBodyData({status: '', search: '', start: 0, end: 0, dtype: "created"});
        setInvoicesList([]);
        setSearchTerm('');
        setSelectedType('created');
        setSelectedDate(null);
        setSelectedStatus('');
        getData(1, {status: '', search: '', start: 0, end: 0, dtype: "created"});
    }
    const submitSearch = (e) =>{
        e.preventDefault();
        if(isLoading){
            return;
        }
        let start = 0;
        let end = 0;
        if(selectedDate && selectedDate.length === 2){
            start = selectedDate[0].setHours(0,0,0,0)
            end = selectedDate[1].setHours(23, 59, 59, 999)
        }
        let body = {
            search: searchTerm,
            dtype: selectedType,
            status: selectedStatus,
            start,
            end
        }
        setBodyData(body);
        setInvoicesList([]);
        getData(1, body);
    }

    const exportCsv = () =>{
        let start = 0;
        let end = 0;
        if(selectedDate && selectedDate.length === 2){
            start = selectedDate[0].getTime()
            end = selectedDate[1].setHours(23, 59, 59, 999)
        }
        let body = {
            search: searchTerm,
            dtype: selectedType,
            status: selectedStatus,
            start,
            end
        }
        const url = `${apiUrl}invoice/listsrecurringcsv?session=${localStorage.getItem('session')}&data=${encodeURIComponent(JSON.stringify(body))}`;
        window.open(url, '_blank');
    }

    return (
        <Stack padding={2} sx={{background: '#d8d9dd', height: '100%', overflow: 'auto'}}>
            <Stack padding={2} pt={4} borderRadius={5} sx={{background: 'linear-gradient(to bottom, #e2e3e5, #cfd6e6)', flex: 1}}>
                <Stack px={2} flexDirection={'row'} justifyContent={'space-between'}>
                    <Typography variant='h4'>Recurring Invoices ({totalDocs})</Typography>
                    <Button variant='contained' onClick={exportCsv} color='info'>Export</Button>
                </Stack>
                <Box component='form' p={2} m={2} sx={{background: '#fff',borderRadius: 3, boxShadow: '5px 3px 5px #00000005'}} onReset={resetList} onSubmit={submitSearch}>
                    <Stack flexDirection='row' gap={2}>
                        <TextField fullWidth variant='outlined' label='Name/Phone/Company Name' value={searchTerm} onChange={(e)=>{setSearchTerm(e.target.value)}} />
                        <FormControl fullWidth>
                            <InputLabel>Date Type</InputLabel>
                            <Select value={selectedType} onChange={(e)=>{setSelectedType(e.target.value)}} >
                                <MenuItem value={'created'}>Created</MenuItem>
                                <MenuItem value={'start'}>Start Date</MenuItem>
                                <MenuItem value={'end'}>End Date</MenuItem>
                                <MenuItem value={'next'}>Next Date</MenuItem>
                            </Select>
                        </FormControl>
                        <Stack>
                            <Box width={300}></Box>
                            <DateRangePicker format="MMM dd, yyyy" character=" - " placeholder="Select Date Range" value={selectedDate} onChange={setSelectedDate} />
                        </Stack>
                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select value={selectedStatus} onChange={(e)=>{setSelectedStatus(e.target.value)}} >
                                <MenuItem value={'Y'}>Active</MenuItem>
                                <MenuItem value={'N'}>Inactive</MenuItem>
                            </Select>
                        </FormControl>
                        <IconButton type='submit'>
                            <SearchTwoTone fontSize='large' color='primary' />
                        </IconButton>
                        <IconButton type='reset'>
                            <Close fontSize='large' color='primary' />
                        </IconButton>
                    </Stack>
                </Box>
                <Stack mt={2.5} flexDirection='row'alignItems='center' width='100%' gap={1} px={3.2}>
                    <Box width='15%'>
                        <Typography><b>Created</b></Typography>
                    </Box>
                    <Box width='20%'>
                        <Typography><b>Customer Name and Company</b></Typography>
                    </Box>
                    <Box width='15%'>
                        <Typography><b>Linked Service</b></Typography>
                    </Box>
                    <Box width='10%'>
                        <Typography><b>Invoice Start Date</b></Typography>
                    </Box>
                    <Box width='10%'>
                        <Typography><b>Invoice End Date</b></Typography>
                    </Box>
                    <Box width='8%'>
                        <Typography><b>Interval</b></Typography>
                    </Box>
                    <Box width='10%'>
                        <Typography><b>Next Invoice Date</b></Typography>
                    </Box>
                    <Box width='6%'>
                        <Typography><b>Status</b></Typography>
                    </Box>
                    <Box width='6%'>
                        <Typography><b>Details</b></Typography>
                    </Box>
                </Stack>

                <Stack mt={1} p={2} pt={0} height='calc(100vh - 300px)' overflow='auto' gap={3} ref={containerRef}>
                    {invoicesList.map(invoice=>{
                        return <Stack key={invoice.id} flexDirection='row' gap={1} p={1.2} alignItems='center' sx={{background:'#fff', border: '1px solid rgb(226, 232, 240)', borderRadius: '9px', boxShadow: '5px 3px 5px #00000005', color: 'rgb(71, 85, 105)'}}>
                            <Box width='15%'>
                                <Typography>{moment(invoice.created).format('Do MMM yyyy h:mm a')}</Typography>
                            </Box>
                            <Stack width={'20%'}>
                                <Box width={'fit-content'}>
                                    <Link to={`/customer/${invoice.customer.id}`} style={{textDecoration: 'none'}}>
                                        <Typography color={'#000'} variant='h6'>{invoice.customer.name}</Typography>
                                    </Link>
                                </Box>
                                <Typography>{invoice.customer.companyName}</Typography>
                            </Stack>
                            <Stack width={'15%'}>
                                {invoice.linkedService && <Typography>{invoice.linkedService.service.name}</Typography>}
                            </Stack>
                            <Box width='10%'>
                                <Typography>{moment(invoice.startDate).format('Do MMM yyyy')}</Typography>
                            </Box>
                            <Box width='10%'>
                                <Typography>{moment(invoice.endDate).format('Do MMM yyyy')}</Typography>
                            </Box>
                            <Box width='8%'>
                                <Typography >{invoice.interval} {invoice.intervalType==="day"?"Days":"Months"}</Typography>
                            </Box>
                            <Box width='10%'>
                                {invoice.nextDate && <Typography>{moment(invoice.nextDate).format('Do MMM yyyy')}</Typography>}
                                {!invoice.nextDate && <Typography fontWeight={'bold'}>Stopped</Typography>}
                            </Box>
                            <Box width={'6%'}>
                                <Typography color={invoice.status?"success":"error"}>{invoice.status?"Active":"Inactive"}</Typography>
                            </Box> 
                            <Box width={'6%'}>
                                <Link to={`/recurringinvoice/${invoice.id}`}>
                                    <Button size='small' variant='contained'>Details</Button>
                                </Link>
                            </Box>
                        </Stack>
                    })}
                    {isLoading && <Stack direction='row' gap={1} sx={{alignItems: 'center', justifyContent: 'center'}}>
                        <ClipLoader size={15} color='#000' />
                        <Typography variant='body'>Loading Invoices</Typography>
                    </Stack>}
                </Stack>
            </Stack>

        </Stack>
    )
}

export default RecurringInvoicePage